import React, { useEffect, useState } from 'react';
import { formatItemAmount, importFromProcore, listFromProcore, sendToProcore } from '../procoreActions';
import {
  asList,
  getJson,
  integrationError,
  integrationLabel,
  isProjectToolIntegration,
  isSyncableIntegration,
} from '../helpers';

function commitmentTitle(row) {
  return [row.commitment_number, row.commitment_name].filter(Boolean).join(' — ') || 'Commitment';
}

function commitmentSubtitle(row) {
  const vendor = row.vendor?.vendor_name || row.vendor?.company_name || row.vendor?.name || row.vendor_name;
  const amount = row.commitment_amount ?? row.original_amount;
  return [vendor, formatItemAmount(amount)].filter(Boolean).join(' · ');
}

export default function CommitmentSyncModal({ projectVuid, onClose, onComplete }) {
  const [integrations, setIntegrations] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [integrationVuid, setIntegrationVuid] = useState('');
  const [direction, setDirection] = useState('');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState('init');
  const [flash, setFlash] = useState(null);

  const integration = integrations.find((row) => row.vuid === integrationVuid) || integrations[0];
  const sendAllowed = isProjectToolIntegration(integration);
  const receiveAllowed = isProjectToolIntegration(integration);

  const resetChoice = () => {
    setDirection('');
    setItems([]);
    setSelected([]);
    setFlash(null);
  };

  useEffect(() => {
    let cancelled = false;
    setBusy('init');
    Promise.all([
      getJson('/api/integrations'),
      getJson('/api/project-commitments', { project_vuid: projectVuid }),
    ])
      .then(([integrationRows, commitmentRows]) => {
        if (cancelled) return;
        const nextIntegrations = asList(integrationRows).filter((row) => {
          if (String(row.status || '').toLowerCase() !== 'active') return false;
          return isSyncableIntegration(row);
        });
        setIntegrations(nextIntegrations);
        setIntegrationVuid(nextIntegrations[0]?.vuid || '');
        setCommitments(asList(commitmentRows).filter((row) => !projectVuid || row.project_vuid === projectVuid));
      })
      .catch((err) => {
        if (!cancelled) setFlash({ type: 'err', text: integrationError(err) });
      })
      .finally(() => {
        if (!cancelled) setBusy('');
      });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    resetChoice();
  }, [integrationVuid]);

  const loadItems = async (nextDirection) => {
    if (!integration) return;
    setBusy('load');
    setFlash(null);
    try {
      if (isProjectToolIntegration(integration) && nextDirection === 'send') {
        const next = commitments.map((row) => ({
          id: row.vuid,
          title: commitmentTitle(row),
          subtitle: commitmentSubtitle(row),
          sendKind: 'commitment',
          payload: { commitment_vuid: row.vuid },
        }));
        setItems(next);
        setSelected(next.map((row) => row.id));
      } else if (isProjectToolIntegration(integration) && nextDirection === 'receive') {
        const result = await listFromProcore('commitments', integration.vuid, projectVuid);
        const next = (result.items || [])
          .filter((row) => !row.alreadyImported)
          .map((row) => ({
            id: row.id,
            title: row.title,
            subtitle: [row.subtitle, formatItemAmount(row.amount)].filter(Boolean).join(' · '),
            importKind: 'commitments',
            listItem: row,
          }));
        setItems(next);
        setSelected(next.map((row) => row.id));
        if (result.warnings?.length) setFlash({ type: 'err', text: result.warnings.slice(0, 4).join('\n') });
      } else {
        setItems([]);
        setSelected([]);
      }
    } catch (err) {
      setItems([]);
      setSelected([]);
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const pickDirection = (nextDirection) => {
    setDirection(nextDirection);
    loadItems(nextDirection);
  };

  const toggleItem = (id) => {
    setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  const runSync = async () => {
    if (!integration || !direction) return;
    const chosen = items.filter((row) => selected.includes(row.id));
    if (!chosen.length) {
      setFlash({ type: 'err', text: 'Select at least one record.' });
      return;
    }
    setBusy('sync');
    setFlash(null);
    const errors = [];
    let sent = 0;
    try {
      if (isProjectToolIntegration(integration) && direction === 'send') {
        for (const row of chosen) {
          try {
            await sendToProcore(integration.vuid, row.sendKind, row.payload);
            sent += 1;
          } catch (err) {
            errors.push(`${row.title}: ${integrationError(err)}`);
          }
        }
      } else if (isProjectToolIntegration(integration) && direction === 'receive') {
        const result = await importFromProcore(
          'commitments',
          integration.vuid,
          projectVuid,
          chosen.map((row) => row.listItem)
        );
        sent += chosen.length;
        (result.errors || []).forEach((item) => errors.push(item));
      }
      const extra = errors.length ? `\n${errors.slice(0, 6).join('\n')}` : '';
      if (!sent && errors.length) {
        setFlash({ type: 'err', text: errors.slice(0, 6).join('\n') });
        return;
      }
      if (errors.length) {
        setFlash({ type: 'err', text: `Synced ${sent} item${sent === 1 ? '' : 's'}.${extra}` });
        return;
      }
      onComplete();
    } catch (err) {
      setFlash({ type: 'err', text: integrationError(err) });
    } finally {
      setBusy('');
    }
  };

  const emptyMessage =
    direction === 'receive'
      ? 'No unsynced work order contracts for this project.'
      : direction === 'send'
        ? 'No commitments on this project to send.'
        : 'No unsynced records for this integration.';

  return (
    <div className="pc-modal-backdrop" onClick={() => busy !== 'load' && busy !== 'sync' && onClose()}>
      <div className="pc-modal" onClick={(event) => event.stopPropagation()}>
        <div className="pc-modal-head">
          <h2>Sync commitments</h2>
          <button type="button" className="pc-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="pc-modal-body">
          <p className="pc-muted" style={{ marginTop: 0 }}>
            Choose an integration, then send Claret commitments or receive unsynced work order contracts from Procore.
          </p>
          {busy === 'init' ? <div className="pc-empty">Loading integrations…</div> : null}
          {busy !== 'init' && integrations.length === 0 ? (
            <p className="pc-flash pc-flash-err">No active integrations found.</p>
          ) : null}
          {busy !== 'init' && integrations.length > 0 ? (
            <>
              <div className="pc-sync-options">
                {integrations.map((row) => (
                  <button
                    key={row.vuid}
                    type="button"
                    className={`pc-choice${integrationVuid === row.vuid ? ' is-selected' : ''}`}
                    onClick={() => {
                      setIntegrationVuid(row.vuid);
                      resetChoice();
                    }}
                  >
                    <strong>{row.integration_name}</strong>
                    <span>{integrationLabel(row)}</span>
                  </button>
                ))}
              </div>
              {integration ? (
                <div className="pc-sync-options">
                  <button
                    type="button"
                    className={`pc-choice${direction === 'send' ? ' is-selected' : ''}`}
                    disabled={!sendAllowed}
                    onClick={() => pickDirection('send')}
                  >
                    <strong>Send</strong>
                    <span>
                      {sendAllowed
                        ? `Push this project’s commitments to ${integrationLabel(integration)}.`
                        : 'Send is only available for the project tool.'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`pc-choice${direction === 'receive' ? ' is-selected' : ''}`}
                    disabled={!receiveAllowed}
                    onClick={() => pickDirection('receive')}
                  >
                    <strong>Receive</strong>
                    <span>
                      {receiveAllowed
                        ? `Pull unsynced work order contracts from ${integrationLabel(integration)}.`
                        : 'Receive is only available for the project tool.'}
                    </span>
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          {busy === 'load' ? <div className="pc-empty">Loading unsynced records…</div> : null}
          {direction && busy !== 'load' && busy !== 'init' ? (
            items.length === 0 ? (
              <div className="pc-empty">{emptyMessage}</div>
            ) : (
              <>
                <div className="pc-import-toolbar">
                  <button
                    type="button"
                    className="pc-btn pc-btn-secondary pc-btn-sm"
                    onClick={() => setSelected(items.map((row) => row.id))}
                  >
                    Select all
                  </button>
                  <button type="button" className="pc-btn pc-btn-secondary pc-btn-sm" onClick={() => setSelected([])}>
                    Clear
                  </button>
                  <span className="pc-muted">{selected.length} selected</span>
                </div>
                <div className="pc-import-list">
                  {items.map((row) => (
                    <label key={row.id} className="pc-import-row">
                      <input
                        type="checkbox"
                        checked={selected.includes(row.id)}
                        onChange={() => toggleItem(row.id)}
                      />
                      <span>
                        <strong>{row.title}</strong>
                        <span className="pc-meta">{row.subtitle}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )
          ) : null}
          {flash ? (
            <div className={`pc-flash ${flash.type === 'ok' ? 'pc-flash-ok' : 'pc-flash-err'}`}>{flash.text}</div>
          ) : null}
        </div>
        <div className="pc-modal-actions">
          <button type="button" className="pc-btn pc-btn-secondary" onClick={onClose} disabled={busy === 'load' || busy === 'sync'}>
            Cancel
          </button>
          <button
            type="button"
            className="pc-btn"
            onClick={runSync}
            disabled={busy === 'load' || busy === 'sync' || busy === 'init' || !direction || !selected.length}
          >
            {busy === 'sync' ? 'Syncing…' : direction === 'receive' ? `Receive ${selected.length}` : `Send ${selected.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
